import { memo, useEffect, useMemo, useState } from 'react';

import ButtonContainer from 'core/button-container';
import cleanTitle from 'utils/clean-title';
import { fetchSearchData } from 'components/landing/selected-note-list/data-fetcher';
import { InfoIcon } from 'components/icons';
import PropTypes from 'prop-types';
import styles from 'styles/SearchBox.module.scss';
import TextInput from 'core/text-input';
import Title from 'core/title';
import { useSearchParam } from 'hooks/useUrlParams';
import { useTranslations } from 'next-intl';
import variables from 'styles/variables.module.scss';
import ViewNoteButton from 'components/blog/view-note-button/view-note-button';
import { WhiteButton } from 'core/buttons/buttons';

const SearchBox = ({ pageCode, title, searchState }) => {
    const {
        searchTerm,
        currentPageSearch: currentPage,
        setSearchTerm,
        setData,
        setShowMoreButton,
        setLoading,
        setError,
        setActiveSearch,
        setCurrentPageSearch
    } = searchState;

    const t = useTranslations('search-box');
    const [name, setName] = useState('');
    const [loadingInternal, setLoadingInternal] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    // Use custom hook for search parameter management
    const [urlSearchTerm, setURLSearchTerm, hasURLSearch] = useSearchParam('q');

    useEffect(() => {
        // Initialize search from URL parameter
        if (hasURLSearch) {
            setName(urlSearchTerm);
            setSearchTerm(urlSearchTerm);
            setActiveSearch(true);
            setCurrentPageSearch(1);
            setLoadingInternal(false);
            setErrorMessage(null);
        }
    }, [urlSearchTerm]);

    const handleSearchSuccess = (result) => {
        if (currentPage === 1) {
            setData(result.results);
        } else {
            setData((prev) => [...prev, ...result.results]);
        }
        setShowMoreButton(result.next_page !== null);
        setCurrentPageSearch(currentPage + 1);
    };

    const handleSearchError = () => {
        setError(true);
        setData([]);
        setShowMoreButton(false);
    };

    const handleLoadingChange = (isLoading) => {
        setLoadingInternal(isLoading);
        if (!isLoading) {
            setLoading(false);
        }
    };

    const getData = (paramDict) => {
        fetchSearchData(
            paramDict,
            handleSearchSuccess,
            handleSearchError,
            handleLoadingChange
        );
    };

    useEffect(() => {
        if (searchTerm !== null && searchTerm !== '') {
            setData([]);
            setLoading(true);
            setError(false);
            setLoadingInternal(true);

            const paramDict = {
                page_code: pageCode,
                search_term: searchTerm,
                is_search: true,
                page: 1
            };

            getData(paramDict);
        }
    }, [searchTerm]);

    const validateInput = (value) => {
        const minLen = 3;
        if (!value || value.trim() === '' || value?.trim().length < minLen) {
            return { error: true, value: null };
        }

        return { error: false, value: value.trim() };
    };

    const handleClick = () => {
        const { error, value } = validateInput(name);

        if (error) {
            setErrorMessage(t('error-name-invalid'));
        } else {
            // Update the search term if it's different to avoid re-fetching the data
            if (value !== searchTerm) {
                setActiveSearch(true);
                setSearchTerm(value);
                setLoadingInternal(true);
                setCurrentPageSearch(1);
                setErrorMessage(null);

                // Update URL using custom hook
                setURLSearchTerm(value);
            }
        }
    };

    const handleOnKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleClick();
        }
    };

    const displayTitle = useMemo(() => {
        return cleanTitle(title);
    }, [title]);

    return (
        <div className={styles.container}>
            <Title content={displayTitle} tag="h1" marginBottom="100px" align alignText/>
            <div className={styles.input_container}>
                <div className={styles.search_box}>
                    <TextInput
                        fieldName="name"
                        placeholder={t('input.placeholder')}
                        setValue={setName}
                        value={name}
                        onKeyDown={handleOnKeyDown}
                    />
                </div>
                <ButtonContainer containerWidth="auto">
                    <WhiteButton
                        onClick={handleClick}
                        label={t('button.label')}
                        propsButton={{
                            style: {
                                fontSize: '16px',
                                fontWeight: '700',
                                zIndex: 1000,
                                display: 'flex',
                                justifyContent: 'center'
                            }
                        }}
                        disabled={loadingInternal}
                        parentLoading={loadingInternal}
                        whiteBorder
                        compact
                    />
                </ButtonContainer>
            </div>

            {errorMessage && (
                <div className={styles.error_message}>
                    <div className={styles.icon}>
                        <InfoIcon color={variables.pinkColor} size={24} />
                    </div>
                    <p>{errorMessage}</p>
                </div>
            )}
            <ViewNoteButton />
        </div>
    );
};

SearchBox.propTypes = {
    pageCode: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    searchState: PropTypes.shape({
        searchTerm: PropTypes.string,
        currentPageSearch: PropTypes.number,
        setSearchTerm: PropTypes.func.isRequired,
        setData: PropTypes.func.isRequired,
        setShowMoreButton: PropTypes.func.isRequired,
        setLoading: PropTypes.func.isRequired,
        setError: PropTypes.func.isRequired,
        setActiveSearch: PropTypes.func.isRequired,
        setCurrentPageSearch: PropTypes.func.isRequired
    }).isRequired
};

export default memo(SearchBox);
